// Назначение вещи и жизненный цикл — локальные справочники enum-кодов backend
// (ItemPurpose / ItemLifecycleStatus). Коды стабильны, локализация — на клиенте.

export type ItemPurpose = 'EVERYDAY' | 'HOME' | 'FORMAL' | 'WORK';

export type ItemLifecycleStatus =
    | 'IN_USE'
    | 'MOVED_TO_HOME'
    | 'MOVED_TO_WORK'
    // Аудит 17.09, D10: вещь в гардеробе, но временно выведена из обычной носки.
    | 'UNDER_REPAIR'
    | 'ON_TRIAL'
    | 'GIVEN_AWAY'
    | 'DONATED'
    | 'DISPOSED'
    | 'RECYCLED'
    | 'SOLD';

export const PURPOSE_OPTIONS: {value: ItemPurpose; label: string}[] = [
    {value: 'EVERYDAY', label: 'Повседневная'},
    {value: 'HOME', label: 'Домашняя'},
    {value: 'FORMAL', label: 'Парадная'},
    {value: 'WORK', label: 'Рабочая'},
];

export const PURPOSE_LABELS: Record<ItemPurpose, string> = Object.fromEntries(
    PURPOSE_OPTIONS.map(o => [o.value, o.label]),
) as Record<ItemPurpose, string>;

export const LIFECYCLE_LABELS: Record<ItemLifecycleStatus, string> = {
    IN_USE: 'Используется',
    MOVED_TO_HOME: 'Переведена в домашние',
    MOVED_TO_WORK: 'Переведена в рабочие',
    UNDER_REPAIR: 'На восстановлении',
    ON_TRIAL: 'На проверке',
    GIVEN_AWAY: 'Отдано',
    DONATED: 'Пожертвовано',
    DISPOSED: 'Выброшено',
    RECYCLED: 'Сдано на переработку',
    SOLD: 'Продано',
};

/** Статусы выбытия — вещь покинула гардероб (thrownAway на backend). */
export const RETIRED_STATUSES: ItemLifecycleStatus[] =
    ['GIVEN_AWAY', 'DONATED', 'DISPOSED', 'RECYCLED', 'SOLD'];

/**
 * Намерение избавления — retired-подмножество ItemLifecycleStatus (disposalIntent на backend).
 * SOLD сюда не входит: продажа выставляется только через подтверждение по объявлению,
 * прямой перевод backend отклоняет (SOLD_REQUIRES_LISTING).
 */
export type DisposalIntent = 'GIVEN_AWAY' | 'DONATED' | 'DISPOSED' | 'RECYCLED';

/** Пункты подменю «Избавиться…» (порядок = порядок в меню). */
export const INTENT_OPTIONS: {value: DisposalIntent; label: string}[] = [
    {value: 'GIVEN_AWAY', label: 'Отдать'},
    {value: 'DONATED', label: 'Пожертвовать'},
    {value: 'RECYCLED', label: 'Сдать на переработку'},
    {value: 'DISPOSED', label: 'Выбросить'},
];

/**
 * Подпись способа выбытия — в инфинитиве («Отдать», «Выбросить») и на кнопке подтверждения,
 * и в архиве: решение владельца 17.09, прошедшее время («Отдал») читалось как отчёт, а не
 * как действие.
 */
export const DISPOSAL_METHOD_LABELS: Record<DisposalIntent, string> = {
    GIVEN_AWAY: 'Отдать',
    DONATED: 'Пожертвовать',
    RECYCLED: 'Сдать на переработку',
    DISPOSED: 'Выбросить',
};

/** Подпись способа выбытия для архива и подтверждений. */
export function disposalMethodLabel(intent: DisposalIntent | null | undefined): string {
    return intent ? DISPOSAL_METHOD_LABELS[intent] ?? 'Выбросить' : 'Выбросить';
}

const PENDING_DISPOSAL_LABELS: Record<DisposalIntent, string> = {
    GIVEN_AWAY: 'будет отдано',
    DONATED: 'будет пожертвовано',
    RECYCLED: 'будет переработано',
    DISPOSED: 'будет выброшено',
};

/** Подпись принятого решения для бейджа «Ждёт передачи · будет …»; без намерения — null. */
export function pendingDisposalLabel(intent: DisposalIntent | null | undefined): string | null {
    return intent ? PENDING_DISPOSAL_LABELS[intent] ?? null : null;
}

/**
 * «Где вещь носится сейчас» для шапки меню жизненного цикла:
 * Повседневная (IN_USE) / Домашняя / Рабочая; для выбывших — null.
 * Ярлык IN_USE — тот же, что у назначения EVERYDAY (R7: одна карта меток, а не
 * «Выходная» в меню против «Повседневная» в параметрах).
 */
export function wearContextLabel(status: ItemLifecycleStatus | null | undefined): string | null {
    if (status === 'MOVED_TO_HOME') return 'Домашняя';
    if (status === 'MOVED_TO_WORK') return 'Рабочая';
    return isRetired(status) ? null : PURPOSE_LABELS.EVERYDAY;
}

/** Поля вещи, от которых зависит её видимый статус. */
export interface ItemStatusSource {
    thrownAway?: boolean | null;
    inTrashBin?: boolean | null;
    lifecycleStatus?: ItemLifecycleStatus | null;
}

/**
 * Строка «Статус» в параметрах вещи (R7): принятое решение и выбытие важнее lifecycle —
 * вещь, ждущая передачи, не должна выглядеть «Используется».
 */
export function itemStatusLabel(item: ItemStatusSource | null | undefined): string | null {
    if (!item) return null;
    if (item.thrownAway) return retirementLabel(item.lifecycleStatus) ?? LIFECYCLE_LABELS.DISPOSED;
    if (item.inTrashBin) return 'Ждёт передачи';
    return item.lifecycleStatus ? LIFECYCLE_LABELS[item.lifecycleStatus] ?? null : null;
}

export function isRetired(status: ItemLifecycleStatus | null | undefined): boolean {
    return !!status && RETIRED_STATUSES.includes(status);
}

/**
 * Вещь на восстановлении или на проверке в носке (аудит 17.09, D10): она в гардеробе, но из
 * обычной носки и из очереди выбытия выведена до даты возврата.
 */
export function isRestoring(status: ItemLifecycleStatus | null | undefined): boolean {
    return status === 'UNDER_REPAIR' || status === 'ON_TRIAL';
}

/** Подпись причины выбытия для бейджа («Отдано/Пожертвовано/Выброшено»); для не-выбывших — null. */
export function retirementLabel(status: ItemLifecycleStatus | null | undefined): string | null {
    return isRetired(status) ? LIFECYCLE_LABELS[status as ItemLifecycleStatus] : null;
}

/**
 * Единый вычисляемый статус доступности вещи (аудит выбытия 17.09.2026, D14). Приходит из
 * DTO вещи готовым: правило «непригодна к носке» живёт на backend в одном месте, клиент его
 * не пересчитывает и не может с ним разойтись.
 */
export type ItemAvailability =
    | 'ACTIVE'
    | 'LIMITED'
    | 'IN_RESTORATION'
    | 'HOME_ONLY'
    | 'WORK_ONLY'
    | 'UNFIT'
    | 'PENDING_DISPOSAL'
    | 'RETIRED';

export const AVAILABILITY_LABELS: Record<ItemAvailability, string> = {
    ACTIVE: 'Активна',
    LIMITED: 'Ограниченно пригодна',
    IN_RESTORATION: 'На восстановлении',
    HOME_ONLY: 'Домашняя',
    WORK_ONLY: 'Рабочая',
    UNFIT: 'Непригодна к носке',
    PENDING_DISPOSAL: 'Ждёт физического выбытия',
    RETIRED: 'Выбыла',
};

/** Подпись статуса: серверная важнее локальной — она источник истины. */
export function availabilityLabel(availability: ItemAvailability | null | undefined,
                                  serverLabel?: string | null): string | null {
    if (serverLabel) return serverLabel;
    return availability ? AVAILABILITY_LABELS[availability] ?? null : null;
}

/** Нужна ли оговорка в интерфейсе: всё, кроме «Активна». */
export function availabilityRestricted(availability: ItemAvailability | null | undefined): boolean {
    return !!availability && availability !== 'ACTIVE';
}
